import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { allProjects, categories } from '@/mocks/projects';
import ProjectCard from './ProjectCard';
import { useProjectI18n } from '@/hooks/useProjectI18n';

export default function ProjectsGrid() {
  const { t } = useTranslation();
  const { isEn } = useProjectI18n();
  const [activeCategory, setActiveCategory] = useState('Tümü');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = allProjects.filter((p) => {
    const matchesCategory = activeCategory === 'Tümü' || p.category === activeCategory;
    const query = searchQuery.toLowerCase();
    if (query === '') return matchesCategory;
    const searchFields = [p.title, p.location, p.client];
    if (isEn) {
      if (p.title_en) searchFields.push(p.title_en);
      if (p.location_en) searchFields.push(p.location_en);
      if (p.description_en) searchFields.push(p.description_en);
    }
    searchFields.push(p.description);
    const matchesSearch = searchFields.some((f) => f.toLowerCase().includes(query));
    return matchesCategory && matchesSearch;
  });

  const getCategoryLabel = (cat: string) => {
    const key = `projects.categories.${cat}`;
    const translated = t(key);
    return translated !== key ? translated : cat;
  };

  return (
    <section className="bg-gray-50 py-16 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-10">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-xs tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap font-medium ${
                  activeCategory === cat
                    ? 'bg-amber-500 text-white'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-amber-300 hover:text-amber-600'
                }`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {getCategoryLabel(cat)}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-auto">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder={t('projects.grid.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-white border border-gray-200 text-gray-700 placeholder-gray-400 text-sm rounded-full pl-9 pr-4 py-2.5 w-full sm:w-64 outline-none focus:border-amber-400 transition-colors duration-200"
              style={{ fontFamily: "'Inter', sans-serif" }}
            />
          </div>
        </div>

        <p className="text-gray-400 text-xs mb-8" style={{ fontFamily: "'Inter', sans-serif" }}>
          {t('projects.grid.count', { count: filtered.length })}
        </p>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        ) : (
          <div className="text-center py-24">
            <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
              <i className="ri-search-line text-gray-400 text-xl" />
            </div>
            <p className="text-gray-500 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
              {t('projects.grid.noResults')}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}